import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { X, Check, XCircle, RotateCw, Loader2, FileSignature, ShieldCheck } from "lucide-react";

// Iter 111 — Admin Contract Review dashboard. UI-only on tested endpoints
// (Iter 110 approve-proof / reject-proof / reopen-proof + new
// /admin/contracts/{cid}/proofs). No backend/state/credit changes.
//
// PII rules — admin sees real aliases + raw account name + presigned proofs.
// This scope stays admin-only (Iter 110 streamer/player projections already
// scrub the fields; this component never proxies them elsewhere).

const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—");

const STAGES = [
  { key: "admin_review",  label: "Pending Review",   tint: "border-[#F5B841]/60 text-[#F5B841]" },
  { key: "proof_rejected", label: "Proof Rejected",  tint: "border-[#F26D6D]/60 text-[#F26D6D]" },
  { key: "active",        label: "Active",           tint: "border-emerald-500/60 text-emerald-400" },
  { key: "finished",      label: "Fulfilled",        tint: "border-white/40 text-white" },
];
const FINISHED = new Set(["finished", "settled", "expired", "declined"]);
const bucketOf = (s) => {
  if (s === "admin_review") return "admin_review";
  if (s === "proof_rejected") return "proof_rejected";
  if (s === "active") return "active";
  if (FINISHED.has(s)) return "finished";
  return null;
};

function StagePill({ status }) {
  const meta = STAGES.find((b) => b.key === bucketOf(status));
  const tint = meta?.tint || "border-white/20 text-[#8B97AE]";
  return <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${tint}`}>{status.replace(/_/g, " ")}</span>;
}

function isImage(ct) { return typeof ct === "string" && ct.startsWith("image/"); }
function isVideo(ct) { return typeof ct === "string" && ct.startsWith("video/"); }

function ReviewModal({ contract, onClose, onActed }) {
  const [proofData, setProofData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);      // "approve" | "reject" | "reopen"
  const [rejectReason, setRejectReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const status = contract.status;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/contracts/${contract.id}/proofs`)
      .then(({ data }) => { if (!cancelled) setProofData(data); })
      .catch((e) => { if (!cancelled) toast.error(apiErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [contract.id]);

  const catEntries = Object.entries(contract.wagerCategories || {});

  const runAction = async (kind, body) => {
    setBusy(kind);
    try {
      const url = `/admin/contracts/${contract.id}/${kind === "approve" ? "approve-proof" : kind === "reject" ? "reject-proof" : "reopen-proof"}`;
      await api.post(url, body || {});
      toast.success(kind === "approve" ? "Approved — contract active" : kind === "reject" ? "Rejected — player notified" : "Reopened — back to player");
      onActed?.();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === "object") {
        toast.error(`State changed — current ${d.current || "?"}. Refresh and try again.`);
      } else {
        toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
      }
    } finally { setBusy(null); }
  };

  const approve = () => runAction("approve");
  const reopen  = () => runAction("reopen");
  const reject  = () => {
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    runAction("reject", { reason: rejectReason.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-review-modal">
      <div className="w-full max-w-3xl bg-[#0B1A14] border border-[#F5B841]/40 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0B1A14]">
          <div className="flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-[#F5B841]" />
            <h3 className="font-display font-bold text-base text-[#E8ECF4]">Contract review</h3>
            <StagePill status={status} />
          </div>
          <button onClick={onClose} data-testid="admin-review-close" className="text-[#8B97AE] hover:text-[#E8ECF4]"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs font-mono">
            <div><div className="text-[9px] uppercase tracking-widest text-[#8B97AE] mb-0.5">Player</div><div>{contract.player_alias || contract.playerId?.slice(0, 8)}</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-[#8B97AE] mb-0.5">Streamer</div><div>{contract.streamer_alias || contract.streamerId?.slice(0, 8)}</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-[#8B97AE] mb-0.5">Submitted</div><div>{(contract.submitted_at || contract.updatedAt || "").slice(0, 16).replace("T", " ")}</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-[#8B97AE] mb-0.5">Agreed weighted</div><div className="text-[#2DD4A7]">{fmt(contract.agreedWeightedWager ?? contract.agreed_monthly_volume)}</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-[#8B97AE] mb-0.5">Reserved</div><div>{fmt(contract.reservedCreditsAmount || 0)} cr</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-[#8B97AE] mb-0.5">Vertical</div><div>{contract.vertical || "—"}</div></div>
          </div>

          {catEntries.length > 0 && (
            <div className="border border-white/10 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-2">Category breakdown</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono">
                {catEntries.map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 py-0.5">
                    <span className="text-[#8B97AE]">{k.replace(/_/g, " ")}</span>
                    <span>{fmt(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border border-[#F5B841]/30 bg-[#F5B841]/[0.04] p-3 text-xs">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-1">Where to register</div>
            <div className="space-y-0.5 font-mono">
              <div>Casino: {contract.casino || "—"}</div>
              <div>Affiliate code: <span className="text-[#F5B841]">{contract.affiliateCode || "—"}</span></div>
              {contract.referralLink && <div>Link: <a href={contract.referralLink} target="_blank" rel="noopener noreferrer" className="text-[#2DD4A7] underline break-all">{contract.referralLink}</a></div>}
            </div>
          </div>

          <div className="border border-white/10 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]">
              <ShieldCheck className="w-3 h-3" />Player proof · admin-only
            </div>
            <div className="text-xs font-mono">
              <span className="text-[#8B97AE]">Casino account name:</span>{" "}
              <span className="text-[#E8ECF4]" data-testid="admin-review-account-name">{loading ? "…" : (proofData?.playerCasinoAccountName || "—")}</span>
            </div>
            <div>
              {loading ? (
                <div className="flex items-center gap-1 text-[10px] text-[#8B97AE]"><Loader2 className="w-3 h-3 animate-spin" />Loading proofs…</div>
              ) : proofData?.files?.length ? (
                <ul className="space-y-2" data-testid="admin-review-proof-list">
                  {proofData.files.map((f, i) => (
                    <li key={f.id} className="border border-white/10 p-2 space-y-1">
                      <div className="flex items-center justify-between text-[11px] font-mono">
                        <span className="text-[#E8ECF4] truncate mr-2">{f.filename}</span>
                        {f.url && <a href={f.url} target="_blank" rel="noopener noreferrer" data-testid={`admin-proof-open-${i}`} className="text-[10px] uppercase tracking-widest text-[#F5B841] hover:text-white">Open</a>}
                      </div>
                      {f.url && isImage(f.contentType) && <img src={f.url} alt="" className="max-h-64 object-contain border border-white/10" />}
                      {f.url && isVideo(f.contentType) && <video src={f.url} controls className="max-h-64 w-full border border-white/10" />}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#F26D6D]">— no proofs recorded —</div>
              )}
            </div>
          </div>

          {status === "proof_rejected" && contract.proof_rejection_reason && (
            <div className="border border-[#F26D6D]/40 bg-[#F26D6D]/[0.06] p-3 text-xs" data-testid="admin-review-reject-reason">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D] mb-1">Previous rejection reason</div>
              <div className="whitespace-pre-wrap break-words">{contract.proof_rejection_reason}</div>
            </div>
          )}

          {status === "admin_review" && (
            <div className="pt-2 border-t border-white/10 space-y-2">
              <div className="flex flex-wrap gap-2">
                <button data-testid="admin-review-approve" onClick={approve} disabled={busy !== null}
                  className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#2DD4A7] text-[#04150F] text-xs font-mono uppercase tracking-widest hover:bg-[#F5B841] transition-colors disabled:opacity-40"><Check className="w-3 h-3" />{busy === "approve" ? "…" : "Approve"}</button>
                <button data-testid="admin-review-reject-open" onClick={() => setRejectOpen(!rejectOpen)} disabled={busy !== null}
                  className="inline-flex items-center gap-1.5 px-4 h-9 border border-[#F26D6D]/60 text-[#F26D6D] text-xs font-mono uppercase tracking-widest hover:bg-[#F26D6D]/[0.08]"><XCircle className="w-3 h-3" />Reject…</button>
              </div>
              {rejectOpen && (
                <div className="border border-[#F26D6D]/40 bg-[#F26D6D]/[0.06] p-3 space-y-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D]">Reason (required)</div>
                  <textarea data-testid="admin-review-reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value.slice(0, 1000))}
                    rows={3} placeholder="Explain what's wrong so the player can fix on reopen…"
                    className="w-full bg-transparent border border-white/20 text-xs text-[#E8ECF4] p-2 font-mono outline-none focus:border-[#F26D6D]" />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setRejectOpen(false); setRejectReason(""); }} className="px-3 h-8 border border-white/20 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] hover:text-[#E8ECF4]">Cancel</button>
                    <button data-testid="admin-review-reject-confirm" onClick={reject} disabled={busy !== null || !rejectReason.trim()}
                      className="px-3 h-8 bg-[#F26D6D] text-white text-[10px] font-mono uppercase tracking-widest hover:opacity-90 disabled:opacity-40">{busy === "reject" ? "…" : "Reject & notify player"}</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {status === "proof_rejected" && (
            <div className="pt-2 border-t border-white/10 flex flex-wrap gap-2">
              <button data-testid="admin-review-reopen" onClick={reopen} disabled={busy !== null}
                className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#2DD4A7] text-[#04150F] text-xs font-mono uppercase tracking-widest hover:bg-[#F5B841] transition-colors disabled:opacity-40"><RotateCw className="w-3 h-3" />{busy === "reopen" ? "…" : "Reopen — send back to player"}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminContractReview({ onAction } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("admin_review");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contracts");
      setRows((data || []).filter((r) => r.flow === "iter3" && bucketOf(r.status)));
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Iter 113 — Notify parent (AdminDashboard) so its aggregated tab-count
  // badges refresh whenever an approve/reject/reopen changes state here.
  const handleActed = useCallback(() => { load(); onAction && onAction(); }, [load, onAction]);

  const grouped = useMemo(() => {
    const g = { admin_review: [], proof_rejected: [], active: [], finished: [] };
    rows.forEach((c) => {
      const b = bucketOf(c.status);
      if (b) g[b].push(c);
    });
    return g;
  }, [rows]);

  const openContract = openId ? rows.find((r) => r.id === openId) : null;
  const visible = filter === "all" ? rows : grouped[filter] || [];

  return (
    <section data-testid="admin-contract-review" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Filter:</div>
        {STAGES.map((s) => (
          <button key={s.key} data-testid={`admin-review-filter-${s.key}`} onClick={() => setFilter(s.key)}
            className={`px-3 h-8 text-[10px] font-mono uppercase tracking-widest border ${filter === s.key ? "bg-white text-black border-white" : "text-[#8B97AE] border-white/20 hover:text-[#E8ECF4]"}`}>
            {s.label} · {grouped[s.key]?.length || 0}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="text-xs text-[#8B97AE] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading contracts…</div>
      ) : visible.length === 0 ? (
        <div className="text-xs text-[#8B97AE] border border-white/10 p-6 text-center">No contracts in this stage.</div>
      ) : (
        <div className="border border-white/10">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
              <tr className="border-b border-white/10 bg-white/[0.02]">
                <th className="text-left p-2">Player</th>
                <th className="text-left p-2">Streamer</th>
                <th className="text-right p-2">Agreed weighted</th>
                <th className="text-left p-2">Casino · code</th>
                <th className="text-left p-2">Submitted</th>
                <th className="text-left p-2">Stage</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id} data-testid={`admin-contract-row-${c.id}`} onClick={() => setOpenId(c.id)}
                    className="border-b border-white/5 last:border-b-0 cursor-pointer hover:bg-white/[0.03]">
                  <td className="p-2 font-mono text-xs">{c.player_alias || c.playerId?.slice(0, 8)}</td>
                  <td className="p-2 font-mono text-xs">{c.streamer_alias || c.streamerId?.slice(0, 8)}</td>
                  <td className="p-2 font-mono text-xs text-right text-[#2DD4A7]">{fmt(c.agreedWeightedWager ?? c.agreed_monthly_volume)}</td>
                  <td className="p-2 font-mono text-xs">{c.casino || "—"} <span className="text-[#F5B841]">/{c.affiliateCode || "—"}</span></td>
                  <td className="p-2 font-mono text-xs text-[#8B97AE]">{(c.submitted_at || c.updatedAt || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2"><StagePill status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openContract && (
        <ReviewModal contract={openContract} onClose={() => setOpenId(null)} onActed={handleActed} />
      )}
    </section>
  );
}
